import { useMemo, useState } from "react";
import { Stack, Box, TextField, Typography, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Tooltip, Skeleton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { isValid } from "date-fns";
import { ChatSession } from "shell/services/types";
import { formatDistanceToNowLocalized } from "shell/i18n/dates";
type ChatHistoryProps = {
  sessions: ChatSession[];
  isLoading: boolean;
  onSelectSession: (chatZUID: string) => void;
  onNewChat: () => void;
};
export const ChatHistory = ({
  sessions,
  isLoading,
  onSelectSession,
  onNewChat
}: ChatHistoryProps) => {
  const {
    t
  } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const filteredChatSessions = useMemo(() => {
    const normalizedSearchTerm = searchTerm?.trim().toLowerCase();
    if (!normalizedSearchTerm) return sessions;
    return sessions.filter(session => (session.title || t("shell.untitledChat")).toLowerCase().includes(normalizedSearchTerm));
  }, [sessions, searchTerm, t]);
  const handleSearch = useMemo(() => debounce((term: string) => setSearchTerm(term), 300), []);
  return <Stack sx={{
    p: 2,
    flex: 1,
    minHeight: 0,
    overflow: "hidden"
  }} data-sentry-element="Stack" data-sentry-component="ChatHistory" data-sentry-source-file="ChatHistory.tsx">
      <Box sx={{
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column"
    }} data-sentry-element="Box" data-sentry-source-file="ChatHistory.tsx">
        <TextField fullWidth data-cy="AIDrawerHistorySearch" placeholder={t("shell.searchChatsPlaceholder")} InputProps={{
        startAdornment: <SearchIcon color="action" />
      }} sx={{
        mb: 2
      }} onChange={e => handleSearch(e.target.value)} data-sentry-element="TextField" data-sentry-source-file="ChatHistory.tsx" />
        <TableContainer sx={{
        flex: 1,
        minHeight: 0
      }} data-sentry-element="TableContainer" data-sentry-source-file="ChatHistory.tsx">
          <Table stickyHeader sx={{
          border: 1,
          borderColor: "border",
          borderRadius: 2,
          tableLayout: "fixed",
          width: "100%"
        }} data-sentry-element="Table" data-sentry-source-file="ChatHistory.tsx">
            <TableHead data-sentry-element="TableHead" data-sentry-source-file="ChatHistory.tsx">
              <TableRow data-sentry-element="TableRow" data-sentry-source-file="ChatHistory.tsx">
                <TableCell sx={{
                bgcolor: "grey.100",
                borderColor: "border"
              }} data-sentry-element="TableCell" data-sentry-source-file="ChatHistory.tsx">
                  <Typography variant="body2" fontWeight={600} data-sentry-element="Typography" data-sentry-source-file="ChatHistory.tsx">
                    {t("shell.chatHistory")}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-sentry-element="TableBody" data-sentry-source-file="ChatHistory.tsx">
              <ChatHistoryRows isLoading={isLoading} sessions={filteredChatSessions} searchTerm={searchTerm} onSelectSession={onSelectSession} data-sentry-element="ChatHistoryRows" data-sentry-source-file="ChatHistory.tsx" />
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Button fullWidth data-cy="AIDrawerNewChat" variant="contained" startIcon={<AddCircleIcon />} sx={{
      mt: 2,
      flexShrink: 0
    }} onClick={onNewChat} data-sentry-element="Button" data-sentry-source-file="ChatHistory.tsx">
        {t("shell.newChat")}
      </Button>
    </Stack>;
};
const CHAT_HISTORY_SKELETON_ROW_COUNT = 3;
type ChatHistoryRowsProps = {
  isLoading: boolean;
  sessions: ChatSession[];
  searchTerm: string;
  onSelectSession: (chatZUID: string) => void;
};
const ChatHistoryRows = ({
  isLoading,
  sessions,
  searchTerm,
  onSelectSession
}: ChatHistoryRowsProps) => {
  const {
    t
  } = useTranslation();
  if (isLoading) {
    return <>
        {Array.from({
        length: CHAT_HISTORY_SKELETON_ROW_COUNT
      }).map((_, index) => <TableRow key={index}>
              <TableCell sx={{
          borderBottom: index === CHAT_HISTORY_SKELETON_ROW_COUNT - 1 ? 0 : 1,
          borderColor: "border",
          display: "flex",
          flexDirection: "column",
          gap: 0.25
        }}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="30%" />
              </TableCell>
            </TableRow>)}
      </>;
  }
  if (!sessions.length) {
    return <TableRow>
        <TableCell>
          <Typography variant="body2">
            {searchTerm ? t("shell.noChatHistoryAvailableForTerm", {
            term: searchTerm
          }) : t("shell.noChatHistoryAvailable")}
          </Typography>
        </TableCell>
      </TableRow>;
  }
  return <>
      {sessions.map((session, index) => {
      const title = session.title || t("shell.untitledChat");
      const updatedDate = session.updatedAt ? new Date(session.updatedAt) : null;
      const updatedAgo = updatedDate && isValid(updatedDate) ? formatDistanceToNowLocalized(updatedDate, {
        addSuffix: true
      }) : "";
      return <TableRow key={session.chatZuid} hover data-cy="ChatHistoryRow" data-chat-zuid={session.chatZuid} onClick={() => onSelectSession(session.chatZuid)} sx={{
        cursor: "pointer"
      }}>
            <TableCell sx={{
          borderBottom: index === sessions.length - 1 ? 0 : 1,
          borderColor: "border",
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          overflow: "hidden"
        }}>
              <Tooltip title={title} disableInteractive enterDelay={500} enterNextDelay={500}>
                <Typography variant="body2" noWrap sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0
            }}>
                  {title}
                </Typography>
              </Tooltip>
              <Typography variant="body3" color="text.disabled" fontWeight={600}>
                {updatedAgo}
              </Typography>
            </TableCell>
          </TableRow>;
    })}
    </>;
};